import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import cn from 'classnames';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Route, Routes, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import PlaceholderLoader from '../../../../website/components/shared/PlaceholderLoaders';
import { ReactComponent as Logo } from '../../assets/icons/logo.svg';
import { ReactComponent as MessagesGoldIcon } from '../../assets/icons/messages-gold.svg';
import { ReactComponent as SearchIcon } from '../../assets/icons/searchIcon.svg';
import { ReactComponent as StarStable } from '../../assets/icons/star-stable.svg';
import { BabylonActions, ReactBabylonLink } from '../../babylon';
import BackButton from '../../components/BackButton/BackButton';
import Button from '../../components/Button/Button';
import CancelIcon from '../../components/CancelIcon/CancelIcon';
import DropdownMenuElemToggle from '../../components/DropDownMenu/DropdownMenuElemToggle/DropdownMenuElemToggle';
import AboutHorses from '../../components/HorsesCard/AboutHorses/AboutHorses';
import IconBox from '../../components/IconBox/IconBox';
import IconWithText from '../../components/IconWithText/IconWithText';
import { displayName } from '../../components/LabelBox/LabelBox';
import Loader from '../../components/Loader/Loader';
import ChatModal, { CHAT_MODAL_VIEWS } from '../../components/Modals/ChatModal/ChatModal';
import DeleteFriendsModal from '../../components/Modals/DeleteFriendsModal/DeleteFriendsModal';
import { pickUserAvatar } from '../../components/PhotoSelect/PhotoSelect';
import { PROFILE_AVATARS } from '../../components/PhotoSelect/PhotoSelectData';
import Heading from '../../components/Typography/Heading/Heading';
import Paragraph from '../../components/Typography/Paragraph/Paragraph';
import { Routes as AppRoutes } from '../../constants/routes';
import { useHorses, useJockeys, useStableRoutes } from '../../hooks/stable';
import useQuery from '../../hooks/useQuery';
import { useAppDispatch, useAppSelector } from '../../store/reducers';
import { getMe, getUserAddress, setVisitedStableUserId } from '../../store/reducers/App/appSlice';
import { setStableIframeHeight, setStyle } from '../../store/reducers/IFrame/iFrameSlice';
import { approveInvite, getFriendStatus, inviteFriend, removeFriendsRequest, SocialActionsName } from '../../store/reducers/Social/slice';
import { getJockeys } from '../../store/reducers/Stable/jockeys';
import { getSkins } from '../../store/reducers/Stable/skins';
import { getHorses, getStableDetails, saveFilterData, setStableDetailsNavigateBackRoute } from '../../store/reducers/Stable/slice';
import { setActiveStarStableTab } from '../../store/reducers/Stable/tabs';
import * as log from '../../utils/log';
import ConfiguratorView from '../ConfiguratorView/ConfiguratorView';
import HorseView from '../HorseView/HorseView';
import JockeyView from '../JockeyView/JockeyView';
import RaceStableList from '../RaceStableList/RaceStableList';
import Stable from './Stable/Stable';
import styles from './StableView.module.scss';
// import {ReactComponent} from "../../assets/icons/sta";
export const buttons = [
    {
        name: 'horses',
        babylonAction: BabylonActions.StablesGoToHorseView
    },
    {
        name: 'jockeys',
        babylonAction: BabylonActions.StablesGoToJockeyView
    }
];
const StableView = ({ canNavigate }) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const [, setParams] = useSearchParams();
    const params = useParams();
    const query = useQuery();
    const { listRoute, stableRoute, horsesListRoute, jockeysListRoute, configuratorRoute, horseRoute, jockeyRoute } = useStableRoutes();
    const { socialStableNavigationBackRoute } = useAppSelector((state) => state.stable);
    const [paramLength, setParamLength] = useState(0);
    const ref = useRef(_jsx(RaceStableList, {}));
    const [stableName, setStableName] = useState('');
    const [deleteFriends, setDeleteFriendsModal] = useState(false);
    const [messagesModal, setMessagesModal] = useState(false);
    const param = useParams();
    const activeStarStable = useAppSelector((state) => state.stableTab.activeStarStable);
    const walletInfo = useAppSelector((state) => state.stable.stableDetails);
    const userInfo = useAppSelector((state) => state.app.userInfo);
    const stableLoading = useAppSelector((state) => state.stable.loading);
    const filtersValue = useAppSelector((state) => state.stable.filtersValue);
    const walletName = useAppSelector((state) => state.wallet.activeWallet.name);
    const pending = useAppSelector((state) => state.social.pending);
    const friendStatus = useAppSelector((state) => state.social.friendStatus);
    const stableElementHeight = useAppSelector((state) => state.iframe.stableElementHeight);
    const address = useAppSelector((state) => state.wallet.activeWallet.address);
    const { notMatchStakeId } = useAppSelector((state) => state.auth);
    const { darkMode } = useAppSelector((state) => state.settings);
    const [stableLogo, setStableLogo] = useState(_jsx(Logo, {}));
    const { stakeId } = params;
    useHorses();
    useJockeys();
    const walletId = useMemo(() => (stakeId ? stakeId : walletName), [stakeId, walletName]);
    const isListView = useMemo(() => pathname === horsesListRoute || pathname === jockeysListRoute || pathname === listRoute, [pathname, listRoute, horsesListRoute, jockeysListRoute]);
    const StableElementRef = useRef(null);
    const isConfiguratorView = useMemo(() => pathname === configuratorRoute, [pathname, configuratorRoute]);
    const isHorseOrJockeyRoute = useMemo(() => {
        return pathname.includes('horse') || pathname.includes('jockey');
    }, [pathname, horseRoute, jockeyRoute]);
    const filtersCount = useMemo(() => Object.values(query).filter(Boolean).length, [query]);
    const hasSecondHeaderRow = useMemo(() => pathname === AppRoutes.STABLES, [pathname, stableRoute]);
    log.debug('StableView canNavigate', canNavigate);
    const getAssets = useCallback((walletId) => {
        dispatch(getHorses({ walletId }));
        dispatch(getJockeys({ walletId }));
        dispatch(getSkins({ walletId }));
    }, [dispatch]);
    //Saving the initial route for later navigating
    useEffect(() => {
        dispatch(getMe());
        if (socialStableNavigationBackRoute) {
            dispatch(setStableDetailsNavigateBackRoute(pathname));
        }
    }, []);
    const makeAvatar = (userData) => {
        if (userData && userData.photo && userData.photo !== 'logo') {
            const path = pickUserAvatar(PROFILE_AVATARS[0].contents, userData.photo);
            return (_jsx("div", Object.assign({ className: cn(styles.logo) }, { children: _jsx("img", { src: path, alt: "Avatar" }) })));
        }
        else {
            return (_jsx("div", Object.assign({ className: cn(styles.logo) }, { children: _jsx(Logo, {}) })));
        }
    };
    useEffect(() => {
        const _logo = param.stakeId && friendStatus ? makeAvatar(friendStatus) : makeAvatar(userInfo);
        setStableLogo(_logo);
    }, [userInfo, friendStatus, param, walletId]);
    //To Prevent having filters in memory when we visit other people's stables
    useEffect(() => {
        return () => {
            dispatch(saveFilterData({}));
        };
    }, [dispatch]);
    useEffect(() => {
        let route;
        if (param.stakeId) {
            route = AppRoutes.STABLES_STAKE_ID.replace(':stakeId', param.stakeId);
        }
        else {
            route = AppRoutes.STABLES;
        }
        if (pathname === route) {
            dispatch(setStableDetailsNavigateBackRoute(route));
        }
    }, [pathname]);
    useEffect(() => {
        if (params.stakeId && params.stakeId !== address) {
            dispatch(getUserAddress(params.stakeId));
            setStableName(displayName((friendStatus === null || friendStatus === void 0 ? void 0 : friendStatus.name) || '', (friendStatus === null || friendStatus === void 0 ? void 0 : friendStatus.address) || ''));
        }
        else {
            setStableName((userInfo === null || userInfo === void 0 ? void 0 : userInfo.name) || '');
            dispatch(setVisitedStableUserId(''));
        }
    }, [userInfo, address, params, t, friendStatus]);
    useEffect(() => {
        if (params.stakeId) {
            dispatch(getFriendStatus(params.stakeId));
        }
    }, [dispatch, params]);
    useEffect(() => {
        if (filtersValue) {
            const params = Object.fromEntries(new URLSearchParams(filtersValue));
            setParamLength(Object.keys(params).length);
        }
    }, [filtersValue, paramLength]);
    useEffect(() => {
        walletId && getAssets(walletId);
    }, [walletId, getAssets]);
    useEffect(() => {
        dispatch(getStableDetails({ id: walletId }));
    }, [dispatch, walletId]);
    useEffect(() => {
        ReactBabylonLink.SetConnection('horseUrl', {
            currentState: null,
            set: (assetId) => {
                var _a;
                navigate(`${horseRoute}/${assetId}`);
                (_a = ReactBabylonLink.StateBus.get('horseUrl')) === null || _a === void 0 ? void 0 : _a.onChangeDoneObservable.notifyObservers(assetId);
            }
        });
        ReactBabylonLink.SetConnection('jockeyUrl', {
            currentState: null,
            set: (assetId) => {
                var _a;
                navigate(`${jockeyRoute}/${assetId}`);
                (_a = ReactBabylonLink.StateBus.get('jockeyUrl')) === null || _a === void 0 ? void 0 : _a.onChangeDoneObservable.notifyObservers(assetId);
            }
        });
        return () => {
            ReactBabylonLink.RemoveConnection('jockeyUrl');
        };
    }, [dispatch, horseRoute, jockeyRoute, navigate]);
    const memberId = useMemo(() => friendStatus === null || friendStatus === void 0 ? void 0 : friendStatus.id, [friendStatus]);
    const [currentSceneView, setCurrentSceneView] = useState('intro');
    const location = window.location.href.split('/');
    if (location[location.length - 2] === 'horse' || location[location.length - 2] === 'jockey') {
        const path = location[location.length - 2] + 's';
        if (currentSceneView !== path) {
            setCurrentSceneView(path);
        }
    }
    const stableDetailsData = {
        Horses: walletInfo === null || walletInfo === void 0 ? void 0 : walletInfo.horsesCount,
        Jockeys: walletInfo === null || walletInfo === void 0 ? void 0 : walletInfo.jockeysCount,
        Skins: walletInfo === null || walletInfo === void 0 ? void 0 : walletInfo.skinsCount,
        Foals: walletInfo === null || walletInfo === void 0 ? void 0 : walletInfo.foalsCount
    };
    const ResetFiltersButton = ({ paramLength }) => (_jsxs("div", Object.assign({ className: cn(styles.reset, 'd-flex align-items-center position-relative') }, { children: [_jsx(CancelIcon, { className: styles.cancel }), _jsx(Paragraph, { size: 2, onClick: () => {
                    setParams({});
                    dispatch(saveFilterData({}));
                }, title: `${t('reset')} (${filtersCount})` })] })));
    const MainTabButton = ({ buttonInfo: { name, babylonAction }, color, disabled }) => (_jsx(Button, { onClick: () => {
            dispatch(setActiveStarStableTab(name));
            babylonAction();
            setCurrentSceneView(name);
        }, size: 'auto', color: color, height: 'sm', borderRadius: true, text: name, disabled: disabled }, name));
    const getColor = (name) => (name === activeStarStable ? 1 : 2);
    const getChat = () => {
        setMessagesModal(true);
    };
    const FriendsButtons = () => (_jsxs("div", Object.assign({ className: cn('d-flex align-items-center', styles.friendButtons) }, { children: [friendStatus && friendStatus.invite === undefined && !(friendStatus === null || friendStatus === void 0 ? void 0 : friendStatus.invited) && (_jsx(_Fragment, { children: !(friendStatus === null || friendStatus === void 0 ? void 0 : friendStatus.isFriend) ? (pending.includes(SocialActionsName.INVITE_FRIEND) ? (_jsx(Button, { size: 'md', height: 'sm', text: "", icon: _jsx(Loader, { isTransparent: true }) })) : (_jsx(Button, { size: 'sm', className: styles.addToFriends, height: 'sm', onClick: () => dispatch(inviteFriend({
                        inviteId: friendStatus === null || friendStatus === void 0 ? void 0 : friendStatus.id,
                        address: params.stakeId || ''
                    })), text: t('add_to_friends') }))) : (_jsx(Button, { color: 5, size: 'sm', className: styles.addToFriends, onClick: () => setDeleteFriendsModal(true), height: 'sm', text: t('remove_friend') })) })), (friendStatus === null || friendStatus === void 0 ? void 0 : friendStatus.invited) &&
                !(friendStatus === null || friendStatus === void 0 ? void 0 : friendStatus.invite) &&
                (pending.includes(SocialActionsName.REMOVE_FRIEND_REQUEST) ? (_jsx(Button, { size: 'md', height: 'sm', text: "", icon: _jsx(Loader, { isTransparent: true }) })) : (_jsx(Button, { size: 'md', color: 2, onClick: () => dispatch(removeFriendsRequest({
                        address: params.stakeId,
                        userId: friendStatus.id
                    })), className: styles.addToFriends, height: 'sm', text: t('cancel_request') }))), (friendStatus === null || friendStatus === void 0 ? void 0 : friendStatus.invite) && (_jsx("div", Object.assign({ className: 'd-flex align-items-center justify-content-between' }, { children: pending.includes(SocialActionsName.APPROVE_INVITE) ? (_jsx(Button, { size: 'md', height: 'sm', text: "", icon: _jsx(Loader, { isTransparent: true }) })) : (_jsx(Button, { size: 'md', className: styles.addToFriends, onClick: () => dispatch(approveInvite({
                        inviteId: friendStatus === null || friendStatus === void 0 ? void 0 : friendStatus.invite.id,
                        address: params.stakeId || ''
                    })), height: 'sm', text: t('accept_friends_request') })) }))), _jsx(MessagesGoldIcon, { className: styles.messages, onClick: getChat })] })));
    const handleBack = () => {
        if (socialStableNavigationBackRoute) {
            navigate(socialStableNavigationBackRoute);
        }
        else {
            navigate(AppRoutes.STABLES);
        }
    };
    const MainStableView = () => (_jsxs(Stable, { children: [_jsxs("div", { children: [_jsx("div", Object.assign({ className: styles.logo }, { children: stableLoading ? (_jsx(PlaceholderLoader, { darkMode: darkMode, type: "rect", width: 60, height: 60 })) : (stableLogo) })), _jsxs("div", Object.assign({ className: cn(styles.heading, 'd-flex align-items-center') }, { children: [stakeId && (_jsx(BackButton, { customCallback: handleBack, className: styles.arrowLeft })), stableLoading ? (_jsx(PlaceholderLoader, { darkMode: darkMode, type: "rect", width: 200, height: 50 })) : params.stakeId && params.stakeId !== address ? (_jsx(Heading, { fontWeight: 500, size: 2, title: stableName.toLocaleUpperCase() })) : (_jsxs("div", Object.assign({ className: styles.title }, { children: [_jsx(Heading, { fontWeight: 500, size: 2, title: (userInfo === null || userInfo === void 0 ? void 0 : userInfo.name) || '' }), _jsx(IconWithText, { className: styles.desc, text: t('my_stable').toLocaleUpperCase(), icon: _jsx(StarStable, {}) })] })))] })), _jsxs("div", Object.assign({ className: styles.switcher }, { children: [_jsx(DropdownMenuElemToggle, { label: t('list_view'), onClick: () => navigate(horsesListRoute), checked: false }), _jsxs("div", Object.assign({ className: 'd-flex align-items-center' }, { children: [_jsx(IconBox, Object.assign({ className: styles.search }, { children: _jsx(SearchIcon, { onClick: () => navigate(horsesListRoute) }) })), filtersCount !== 0 ? (_jsx(ResetFiltersButton, { paramLength: paramLength })) : null] }))] })), _jsx("div", Object.assign({ className: cn(styles.buttons, 'd-flex align-items-center') }, { children: buttons.map((buttonInfo) => {
                            const count = walletInfo
                                ? buttonInfo.name === 'horses'
                                    ? walletInfo.horsesCount
                                    : walletInfo.jockeysCount
                                : 0;
                            log.debug(`Count of ${buttonInfo.name} = ${count}`);
                            return (_jsx(MainTabButton, { buttonInfo: buttonInfo, color: getColor(buttonInfo.name), disabled: !canNavigate || count === 0 }, buttonInfo.name));
                        }) }))] }), _jsxs("div", Object.assign({ className: styles.horseDetails }, { children: [_jsx(Heading, { title: t('stable_details') }), stableLoading ? (_jsx(PlaceholderLoader, { darkMode: darkMode, type: "rect", width: 250, height: 40 })) : (_jsx(AboutHorses, { className: styles.stableDetails, elem: stableDetailsData })), stakeId && stakeId !== address && !stableLoading ? _jsx(FriendsButtons, {}) : _jsx(_Fragment, {})] })), deleteFriends && !stableLoading && (_jsx(DeleteFriendsModal, { stakeId: params.stakeId, userId: friendStatus === null || friendStatus === void 0 ? void 0 : friendStatus.id, closeModal: () => setDeleteFriendsModal(false), userName: displayName((friendStatus === null || friendStatus === void 0 ? void 0 : friendStatus.name) || '', (friendStatus === null || friendStatus === void 0 ? void 0 : friendStatus.address) || '') })), messagesModal && (_jsx(ChatModal, { singleChat: true, guildChat: false, memberId: memberId, chatViews: CHAT_MODAL_VIEWS.CHAT_VIEW, closeModal: () => setMessagesModal(false) }))] }));
    useEffect(() => {
        const iFrameStyle = {
            width: isHorseOrJockeyRoute
                ? 'calc(100vw - 530px)'
                : isConfiguratorView
                    ? '100vw'
                    : 'calc(100vw - 530px)',
            position: 'absolute',
            top: isHorseOrJockeyRoute
                ? '78px'
                : isConfiguratorView
                    ? '70px'
                    : hasSecondHeaderRow
                        ? '111px'
                        : '78px',
            left: isConfiguratorView ? '0' : '570px',
            right: '0',
            // Calculated Optimal Height, This helps to not break UI when zooming in and out
            height: isHorseOrJockeyRoute
                ? `${stableElementHeight}px`
                : isConfiguratorView
                    ? 'calc(100% - 209px)'
                    : stableElementHeight
                        ? `${stableElementHeight}px`
                        : '100vh',
            bottom: isConfiguratorView ? '353px' : '0'
        };
        dispatch(setStyle(iFrameStyle));
        return;
    }, [
        isListView,
        isConfiguratorView,
        hasSecondHeaderRow,
        stableElementHeight,
        dispatch,
        pathname
    ]);
    const handleResize = () => {
        var _a;
        dispatch(setStableIframeHeight((_a = StableElementRef.current) === null || _a === void 0 ? void 0 : _a.scrollHeight));
    };
    useEffect(() => {
        handleResize();
    }, []);
    useEffect(() => {
        handleResize();
    }, [pathname, stableLoading]);
    useEffect(() => {
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [pathname, dispatch]);
    return (_jsx("div", Object.assign({ className: cn(styles.element, 'd-flex', isListView && styles['isListView'], isConfiguratorView && styles['isConfiguratorView']), ref: StableElementRef }, { children: _jsxs(Routes, { children: [_jsx(Route, { index: true, element: _jsx(MainStableView, {}) }), _jsx(Route, { path: '/list', element: ref.current }), _jsx(Route, { path: '/list/horses', element: ref.current }), _jsx(Route, { path: '/list/horses', element: _jsx(RaceStableList, { activeTab: "horses" }) }), _jsx(Route, { path: '/list/jockeys', element: _jsx(RaceStableList, { activeTab: "jockeys" }) }), _jsx(Route, { path: '/horse/:id', element: _jsx(HorseView, {}) }), _jsx(Route, { path: '/jockey/:id', element: _jsx(JockeyView, {}) }), _jsx(Route, { path: '/configurator', element: _jsx(ConfiguratorView, { canNavigate: canNavigate }) })] }) })));
};
export default StableView;
